import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { INVESTMENTS as MOCK_INVESTMENTS } from "../mock";
import { getIcon } from "../lib/icons";
import { getOffer, getOffers } from "../lib/api";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ShieldCheck,
  TrendingUp,
  Clock,
  Wallet,
  AlertTriangle,
  Flame,
  Loader2,
} from "lucide-react";

export default function InvestmentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setInv(null);
    setNotFound(false);
    Promise.all([getOffer(slug), getOffers()])
      .then(([detail, all]) => {
        if (!alive) return;
        setInv(detail);
        setRelated(
          all.filter((i) => i.category === detail.category && i.slug !== detail.slug).slice(0, 3)
        );
      })
      .catch((err) => {
        if (!alive) return;
        // Fallback to mock
        const fallback = MOCK_INVESTMENTS.find((i) => i.slug === slug);
        if (fallback) {
          setInv(fallback);
          setRelated(
            MOCK_INVESTMENTS.filter(
              (i) => i.category === fallback.category && i.slug !== fallback.slug
            ).slice(0, 3)
          );
        } else if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setNotFound(true);
        }
      });
    return () => { alive = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-5 py-32 text-center">
          <h1 className="text-4xl font-extrabold text-[#0d1a2e]">Offer not found</h1>
          <p className="mt-3 text-[#0d1a2e]/60">This offer has either closed or never existed.</p>
          <Link to="/investments" className="inline-block mt-6 font-bold text-[#16d196] underline">Back to all offers</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (inv === null) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex justify-center py-32">
          <Loader2 className="w-6 h-6 text-[#16d196] animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = getIcon(inv.icon);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative bg-[#0d1a2e] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[36rem] h-[36rem] glow-emerald pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-12 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[13px] text-white/65 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="mt-8 flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#16d196] flex items-center justify-center shrink-0">
              <Icon className="w-7 h-7 text-[#0d1a2e]" strokeWidth={2.2} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
                  {inv.category} · Live offer
                </div>
                {inv.hot && (
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-[#0d1a2e] bg-[#fde68a] rounded-full px-2 py-0.5">
                    <Flame className="w-3 h-3" strokeWidth={2.6} /> HOT
                  </div>
                )}
              </div>
              <h1 className="mt-2 text-[40px] lg:text-[60px] font-extrabold leading-[1.05] tracking-[-0.025em]">
                {inv.name}
              </h1>
              <p className="mt-3 text-white/70 text-[17px] lg:text-[18px] max-w-2xl">{inv.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <h2 className="text-[24px] lg:text-[30px] font-extrabold text-[#0d1a2e] tracking-tight">
              About this offer
            </h2>
            <p className="mt-4 text-[#0d1a2e]/70 text-[16px] leading-relaxed">{inv.desc}</p>

            <h2 className="mt-12 text-[24px] lg:text-[30px] font-extrabold text-[#0d1a2e] tracking-tight">
              Key highlights
            </h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {inv.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-3 bg-[#f7faf9] border border-[#0d1a2e]/8 rounded-xl px-4 py-3.5"
                >
                  <span className="w-6 h-6 rounded-full bg-[#16d196] flex items-center justify-center mt-0.5 shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#0d1a2e]" strokeWidth={3} />
                  </span>
                  <span className="text-[14.5px] text-[#0d1a2e]/85 font-medium">{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 bg-[#fff7e6] border border-[#f59e0b]/30 rounded-2xl p-5 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-[#d97706] shrink-0 mt-0.5" />
              <div className="text-[13.5px] text-[#0d1a2e]/80 leading-relaxed">
                <strong className="font-extrabold">Risk warning.</strong> The value
                of investments can fall as well as rise. Past performance is not a
                reliable indicator of future results. Capital is at risk and may not
                be protected by the FSCS unless explicitly stated. Tax treatment
                depends on individual circumstances.
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 bg-white border border-[#0d1a2e]/10 rounded-2xl p-6 shadow-[0_12px_36px_rgba(13,26,46,0.06)]">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#0d1a2e]/55 font-bold">
                    Target yield
                  </div>
                  <div className="text-[34px] font-extrabold text-[#0d1a2e] tracking-[-0.02em] leading-none mt-1">
                    {inv.yieldRange}
                  </div>
                </div>
                <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#16d196] font-bold bg-[#e3f7ee] rounded-full px-2.5 py-1">
                  {inv.risk} risk
                </span>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#0d1a2e]/8 pt-5">
                <Stat Icon={Wallet} label="From" value={inv.minEntry} />
                <Stat Icon={Clock} label="Term" value={inv.term} />
                <Stat Icon={TrendingUp} label="Category" value={inv.category} />
                <Stat Icon={ShieldCheck} label="Status" value="Open" />
              </div>

              <Link to={`/apply?type=${inv.slug}`} className="block mt-6">
                <button className="w-full inline-flex items-center justify-center gap-1.5 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-13 py-3.5 text-[14.5px] font-bold transition-colors">
                  Request investor pack
                  <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                </button>
              </Link>
              <p className="mt-3 text-[11.5px] text-[#0d1a2e]/55 text-center">
                Free, no obligation · 60-second form
              </p>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#f7faf9]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="text-[28px] lg:text-[36px] font-extrabold text-[#0d1a2e] mb-8 tracking-tight">
              Related offers
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => {
                const RIcon = getIcon(r.icon);
                return (
                  <Link
                    key={r.slug}
                    to={`/investments/${r.slug}`}
                    className="group bg-white border border-[#0d1a2e]/10 rounded-2xl p-6 hover:border-[#16d196]/60 hover:shadow-[0_12px_36px_rgba(13,26,46,0.08)] transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#e3f7ee] flex items-center justify-center group-hover:bg-[#16d196] transition-colors">
                      <RIcon className="w-5 h-5 text-[#0d1a2e]" strokeWidth={2.2} />
                    </div>
                    <h3 className="mt-4 text-[17px] font-extrabold text-[#0d1a2e] tracking-tight">{r.name}</h3>
                    <p className="mt-1 text-[13px] text-[#0d1a2e]/60">{r.short}</p>
                    <div className="mt-4 text-[13px] font-bold text-[#0d1a2e] group-hover:text-[#16d196] inline-flex items-center gap-1">
                      View offer <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function Stat({ Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] text-[#0d1a2e]/55 font-bold">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-[14px] font-extrabold text-[#0d1a2e] mt-1">{value}</div>
    </div>
  );
}
